import React from "react";

/**
 * WhatsApp's own text formatting, drawn the way the app draws it:
 * *bold*, _italic_, ~strikethrough~, `inline code` and ```monospace```.
 *
 * Same rules as WhatsApp: a marker only counts when it hugs non-space text
 * and sits on a word boundary, so `first_name` or `2*3*4` stay as typed.
 * Bold, italic and strikethrough nest; code does not.
 */

const PATTERN =
  /```([\s\S]+?)```|`([^`\n]+)`|\*(\S(?:[^*\n]*?\S)?)\*|_(\S(?:[^_\n]*?\S)?)_|~(\S(?:[^~\n]*?\S)?)~/g;
const URL_PATTERN = /(https?:\/\/[^\s]+)/g;
const WORD = /[\p{L}\p{N}]/u;

const MONO = {
  fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "0.92em",
};

const linkify = (text, key) =>
  text.split(URL_PATTERN).map((part, index) =>
    index % 2 ? (
      <a key={`${key}-${index}`} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      part
    )
  );

const format = (text, key = "f") => {
  const nodes = [];
  let cursor = 0;
  let n = 0;
  // A fresh regex per call: the recursion below would otherwise move a
  // shared one's lastIndex out from under this loop.
  const pattern = new RegExp(PATTERN.source, "g");
  let match = pattern.exec(text);

  for (; match; match = pattern.exec(text), n += 1) {
    const { index } = match;
    const whole = match[0];
    const before = text[index - 1];
    const after = text[index + whole.length];
    // "snake_case_name" or "a*b*c": markers inside a word are literal, and
    // the next marker along may still open a real span.
    if ((before && WORD.test(before)) || (after && WORD.test(after))) {
      pattern.lastIndex = index + 1;
      continue;
    }

    if (index > cursor) nodes.push(...linkify(text.slice(cursor, index), `${key}-t${n}`));
    const [block, code, bold, italic, strike] = match.slice(1);
    const id = `${key}-${n}`;
    if (block !== undefined)
      nodes.push(
        <span key={id} style={{ ...MONO, whiteSpace: "pre-wrap" }}>
          {block}
        </span>
      );
    else if (code !== undefined)
      nodes.push(
        <code
          key={id}
          style={{ ...MONO, background: "rgba(0,0,0,0.06)", borderRadius: 3, padding: "0 3px" }}
        >
          {code}
        </code>
      );
    else if (bold !== undefined) nodes.push(<strong key={id}>{format(bold, id)}</strong>);
    else if (italic !== undefined) nodes.push(<em key={id}>{format(italic, id)}</em>);
    else nodes.push(<del key={id}>{format(strike, id)}</del>);
    cursor = index + whole.length;
  }

  if (cursor < text.length) nodes.push(...linkify(text.slice(cursor), `${key}-end`));
  return nodes;
};

const WhatsAppText = ({ text, style }) => {
  if (!text) return null;
  return (
    <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", ...style }}>
      {format(String(text))}
    </span>
  );
};

export default WhatsAppText;
